import React, { useState, useMemo } from "react";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { Checkbox, IconButton } from "@mui/material";
import exportIcon from "../../Assets/rank-tracking-assets/export-file.svg";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import {
  getCountryFlag,
  getLanguageName,
} from "../../../helperJs/getCountryFlag";
import { ZustandStore } from "../../../store/zustand-store";
import { useNavigate } from "react-router-dom";
import { CSVLink } from "react-csv";
import {
  formatNumberWithSymbolsNoDecimal,
  formatNumberWithSymbols,
  handleInvitedUserProject,
} from "../../../helperJs/helper";
import Spinner from "../../share/components/spinner";
import TableSettings from "../../share/tables/DynamicTable/table-settings";
import { curday } from "../../share/upDater/constant";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import DeleteIcon from "@mui/icons-material/Delete";
import SemButton from "../../../common/components/Button";
import Ellipsis from "@semcore/ellipsis";
import AddProject from "../../share/components/add-project-section/rank-tracker/AddProject";

function createData(
  language_code,
  project_name,
  project_id,
  project_location,
  avg_pos,
  top_pos,
  domain_rank,
  referring_domains,
  organic_keyword,
  organic_traffic,
  backlinks,
  sub_project
) {
  return {
    language_code,
    project_name,
    project_id,
    project_location,
    avg_pos,
    top_pos,
    domain_rank,
    referring_domains,
    organic_keyword,
    organic_traffic,
    backlinks,
    sub_project,
  };
}

const ProjectTable = ({ setShowDeleteAlert }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user_type = localStorage.getItem("user_type");
  const [open, setOpen] = React.useState(false);
  const [selectedProject, setSelectedProject] = useState([]);
  const {
    project_section,
    userAccessData,
    projectTable,
    updateProjectTableCol,
    emptyKeywordData,
    emptyAllLocData,
    emptyTagData,
  } = ZustandStore((state) => state);
  const page_data = userAccessData?.[0]?.access;
  const websiteurl = useSelector((state) => state.websiteurl);

  const handleCollapse = (domain) => {
    if (domain === open) {
      setOpen("");
    } else {
      setOpen(domain);
    }
  };

  const columnArr = [
    {
      id: "projects",
      label: t("projects.table_columns.projects"),
      minWidth: 250,
      show: !projectTable.includes(t("projects.table_columns.projects")),
      ignore: true,
    },
    {
      id: "avg_pos",
      label: t("projects.table_columns.avg_pos"),
      minWidth: 120,
      align: "center",
      show: !projectTable.includes(t("projects.table_columns.avg_pos")),
      ignore: false,
    },
    {
      id: "top_pos",
      label: t("projects.table_columns.top_pos"),
      minWidth: 120,
      align: "center",
      show: !projectTable.includes(t("projects.table_columns.top_pos")),
      ignore: false,
    },
    {
      id: "domain_rank",
      label: t("projects.table_columns.domain_rank"),
      minWidth: 120,
      align: "center",
      show: !projectTable.includes(t("projects.table_columns.domain_rank")),
      ignore: false,
    },
    {
      id: "referring_domains",
      label: t("projects.table_columns.referring_domains"),
      minWidth: 140,
      align: "center",
      show: !projectTable.includes(
        t("projects.table_columns.referring_domains")
      ),
      ignore: false,
    },
    {
      id: "organic_keyword",
      label: t("projects.table_columns.organic_keyword"),
      minWidth: 140,
      align: "center",
      show: !projectTable.includes(t("projects.table_columns.organic_keyword")),
      ignore: false,
    },
    {
      id: "organic_traffic",
      label: t("projects.table_columns.organic_traffic"),
      minWidth: 140,
      align: "center",
      show: !projectTable.includes(t("projects.table_columns.organic_traffic")),
      ignore: false,
    },
    {
      id: "backlinks",
      label: t("projects.table_columns.backlinks"),
      minWidth: 140,
      align: "center",
      ignore: false,
      show: !projectTable.includes(t("projects.table_columns.backlinks")),
    },
  ];

  const columns = useMemo(() => {
    if (projectTable?.length > 0) {
      return columnArr?.filter((column) => {
        return !projectTable?.includes(column.label);
      });
    } else {
      return columnArr;
    }
  }, [projectTable, t]);

  const project_data = useMemo(() => {
    const excludedProjects = handleInvitedUserProject("False", page_data);
    const data = project_section.data;
    const access_data = data?.filter((project) => {
      return !excludedProjects?.includes(
        `${project.proj_name}, ${getLanguageName(project.language_code)}`
      );
    });

    const filter_data = excludedProjects?.length > 0 ? access_data : data;
    // Grouping objects by proj_name
    const row_data = filter_data?.map((project) => {
      const history_list = project?.backlinks?.data?.[0]?.history_list;
      return {
        proj_name: project?.proj_name,
        language_code: project.language_code,
        id: project.id,
        location_name: project.location_name,
        avg_pos: project.avg_rank,
        top_3: project?.top_3,
        top_10: project?.top_10,
        top_100: project?.top_100,
        domain_rank: project?.domain_authority,
        reff_domain:
          project?.backlinks?.data?.[0]?.history_list?.[
            history_list?.length - 1
          ]?.referring_domains || 0,
        organic_keyword: {
          curr:
            project?.domain_overview?.metrics?.[0]?.metrics?.organic?.count ||
            0,
          last:
            project?.domain_overview?.metrics?.[1]?.metrics?.organic?.count ||
            0,
        },

        organic_traffic: {
          curr:
            project?.domain_overview?.metrics?.[0]?.metrics?.organic?.etv || 0,
          last:
            project?.domain_overview?.metrics?.[1]?.metrics?.organic?.etv || 0,
        },
        backlinks: {
          curr: project?.backlinks?.data?.[0]?.history_list?.[
            history_list?.length - 1
          ]?.backlinks,
          last: project?.backlinks?.data?.[0]?.history_list?.[
            history_list?.length - 2
          ]?.backlinks,
        },
      };
    });

    const groupedData = {};

    row_data.forEach((item) => {
      const project = `${item.proj_name}, ${getLanguageName(
        item.language_code
      )}`;
      if (!groupedData[project]) {
        groupedData[project] = [];
      }
      groupedData[project].push(item);
    });

    // Creating the new structure
    const result = [];

    Object.keys(groupedData).forEach((key) => {
      const [first, ...rest] = groupedData[key];
      if (rest.length > 0) {
        first.sub_list = rest;
      }
      result.push(first);
    });
    return result;
  }, [project_section, page_data]);

  function analyzeValues(curr, last) {
    const difference = curr - last;
    const increased = difference > 0;
    const diff = Math.abs(difference).toFixed(0);
    return (
      <div className="d-flex align-items-center justify-content-center">
        {formatNumberWithSymbols(curr)}
        {curr === last ? null : increased ? (
          <>
            <span className="text-success mx-1">&#8593;</span>
            <span className="text-success d-flex align-items-center">
              {formatNumberWithSymbols(diff)}
            </span>
          </>
        ) : (
          <>
            <span className="text-danger mx-1">&#8595;</span>
            <span className="text-danger">{formatNumberWithSymbols(diff)}</span>
          </>
        )}
      </div>
    );
  }

  const rows = project_data?.map((project) => {
    return createData(
      project.language_code,
      project.proj_name,
      project.id,
      project.location_name,
      project.avg_pos ? Number(project.avg_pos).toFixed(2) : 0,
      `${project.top_3}/${project.top_10}/${project.top_100}`,
      project.domain_rank,
      formatNumberWithSymbolsNoDecimal(project.reff_domain),
      analyzeValues(project.organic_keyword.curr, project.organic_keyword.last),
      analyzeValues(project.organic_traffic.curr, project.organic_traffic.last),
      analyzeValues(project.backlinks.curr, project.backlinks.last),
      project.sub_list?.map((sub_project) => {
        return createData(
          sub_project.language_code,
          sub_project.proj_name,
          sub_project.id,
          sub_project.location_name,
          sub_project.avg_pos ? Number(sub_project.avg_pos).toFixed(2) : 0,
          `${sub_project.top_3}/${sub_project.top_10}/${sub_project.top_100}`,
          sub_project.domain_rank,
          formatNumberWithSymbolsNoDecimal(sub_project.reff_domain),
          analyzeValues(
            sub_project.organic_keyword.curr,
            sub_project.organic_keyword.last
          ),
          analyzeValues(
            sub_project.organic_traffic.curr,
            sub_project.organic_traffic.last
          ),
          analyzeValues(sub_project.backlinks.curr, sub_project.backlinks.last)
        );
      })
    );
  });

  const projectCSVData = useMemo(() => {
    if (project_section?.data?.length === 0) return [];
    return project_section.data?.map((project) => {
      const history_list = project?.backlinks?.data?.[0]?.history_list;
      return {
        project_name: project?.proj_name,
        language_code: project.language_code,
        project_id: project.id,
        project_location: project.location_name,
        avg_pos: formatNumberWithSymbols(project.avg_rank),
        top_3: project?.top_3,
        top_10: project?.top_10,
        top_100: project?.top_100,
        domain_rank: project?.domain_authority,
        reff_domain:
          formatNumberWithSymbols(
            project?.backlinks?.data?.[0]?.history_list?.[
              history_list?.length - 1
            ]?.referring_domains
          ) || 0,
        organic_keyword:
          formatNumberWithSymbols(
            project?.domain_overview?.metrics?.[0]?.metrics?.organic?.count
          ) || 0,

        organic_traffic:
          formatNumberWithSymbols(
            project?.domain_overview?.metrics?.[0]?.metrics?.organic?.etv
          ) || 0,

        backlinks: formatNumberWithSymbols(
          project?.backlinks?.data?.[0]?.history_list?.[
            history_list?.length - 1
          ]?.backlinks
        ),
      };
    });
  }, [project_section?.data]);

  const loadCSVData = () => {
    if (project_section?.data?.length === 0) return [];
    if (selectedProject?.length === 0) {
      return projectCSVData;
    } else {
      return selectedProject;
    }
  };

  const selectedProjectId = useMemo(() => {
    const uniqueProjects = Array.from(
      new Map(
        selectedProject.map((project) => [project.project_id, project])
      ).values()
    );
    return uniqueProjects;
  }, [selectedProject]);

  const isAllProjectSelected = useMemo(() => {
    const selected = selectedProject || [];
    const allKeywords = projectCSVData || [];
    return selected.length === 0
      ? false
      : allKeywords.length === selected.length;
  }, [selectedProject, projectCSVData]);

  const handleSelectAllProject = () => {
    if (selectedProject?.length === 0) {
      setSelectedProject(projectCSVData);
    } else {
      setSelectedProject([]);
    }
  };

  const isKeywordSelected = (id) => {
    const data = selectedProject?.find((item) => item.project_id === id);
    return data ? true : false;
  };

  const handleSelectProjectFromTable = (obj) => {
    const project = projectCSVData?.filter(
      (item) => item.project_id === obj.project_id
    );

    const currProject = selectedProject?.find(
      (item) => item.project_id === obj.project_id
    );

    if (currProject) {
      const projects = selectedProject?.filter(
        (item) => item.project_id !== obj.project_id
      );
      setSelectedProject(projects);
    } else {
      setSelectedProject([...selectedProject, ...project]);
    }
  };

  const handleDeleteProject = () => {
    const id = selectedProjectId?.[0]?.project_id;
    setShowDeleteAlert({
      show: true,
      id,
    });
  };

  const handleProjectChange = (project) => {
    const select_project = `${project.project_name}, ${getLanguageName(
      project.language_code
    )}`;
    navigate("/rank-tracker");
    if (select_project === websiteurl) return;
    sessionStorage.setItem("LAST_PRPJECT_SELECTED", select_project);
    ZustandStore.setState({
      selected_date: null,
      selected_rank_data: {
        data: [],
        loaded: false,
      },
    });
    emptyTagData();
    emptyAllLocData();
    emptyKeywordData();
    dispatch({
      type: "USERCURRENTPROJECTLOCATION",
      payload: [],
    });
    dispatch({ type: "WEBSITEURL", payload: select_project });
    dispatch({ type: "CURRENTLOCATIONCODE", payload: null });
    dispatch({ type: "CURRENTLOCATION", payload: null });
  };

  return (
    <>
      <div className="mb-4 project-table-contl-header">
        <AddProject />
        {user_type === "organization" ? null : (
          <SemButton
            title={t("projects.delete_project_button.tooltip")}
            icon={<DeleteIcon />}
            onClick={() => handleDeleteProject()}
            name={t("projects.delete_project_button.title")}
            disabled={selectedProjectId?.length != 1}
          />
        )}
        <TableSettings
          {...{
            columns: columnArr,
            updateProjectTableCol,
          }}
        />
        <CSVLink
          data={loadCSVData()}
          filename={t("projects.export_csv_button.filename", {
            current_day: curday(),
          })}
        >
          <SemButton
            title={t("projects.export_csv_button.tooltip")}
            disabled={project_section?.data?.length === 0}
            name={t("projects.export_csv_button.title")}
            icon={<img src={exportIcon} alt="exportIcon" />}
            color="#e95655"
          />
        </CSVLink>
      </div>

      <section className="project-table vir-table-border mt-3">
        <Paper
          sx={{
            backgroundColor: "background.paper",
            boxShadow: "none",
            "& .MuiTableContainer-root": {
              boxShadow: "none",
            },
          }}
        >
          <TableContainer>
            <Table stickyHeader aria-label="sticky table" className="vir-table">
              <TableHead>
                <TableRow>
                  {columns.map((column) => {
                    if (column.label === t("projects.table_columns.projects")) {
                      return (
                        <TableCell
                          align="left"
                          variant="head"
                          key={`table-header${column.id}-${column?.label}`}
                          sx={{
                            fontSize: "0.9em",
                            color: "#191B23",
                            backgroundColor: "#F4F5F9",
                            padding: "5px 8px",
                          }}
                          className="table-fixed-th font-medium"
                          style={{
                            minWidth: column.minWidth,
                          }}
                        >
                          <Checkbox
                            className="p-1 me-2"
                            sx={{
                              color: "#C4C7CF",
                              "&.Mui-checked": {
                                color: "#365486",
                              },
                            }}
                            checked={isAllProjectSelected}
                            onChange={handleSelectAllProject}
                          />

                          <span align={column.align}>{column.label}</span>
                        </TableCell>
                      );
                    } else {
                      return (
                        <TableCell
                          key={`table-header${column.id}-${column?.label}`}
                          variant="head"
                          align="center"
                          sx={{
                            fontSize: "0.9em",
                            color: "#191B23",
                            backgroundColor: "#F4F5F9",
                            padding: "5px 8px",
                          }}
                          className="font-medium"
                          style={{ minWidth: column.minWidth }}
                        >
                          {column.label}
                        </TableCell>
                      );
                    }
                  })}
                </TableRow>
              </TableHead>
              <TableBody>
                {project_section?.loading ||
                (rows?.length === 0 && project_section?.data?.length > 0) ? (
                  <TableRow>
                    <TableCell colSpan={columns?.length + 1}>
                      <Spinner />
                    </TableCell>
                  </TableRow>
                ) : project_section?.data?.length === 0 &&
                  project_section?.error ? (
                  <TableRow>
                    <TableCell colSpan={columns?.length + 1}>
                      <div className="d-flex align-items-center justify-content-center flex-column">
                        <p className="mb-1">{t("projects.table_no_data")}</p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  rows?.map((row) => {
                    return (
                      <React.Fragment
                        key={`${row?.project_name}-${row.language_code}`}
                      >
                        <TableRow
                          hover
                          padding="checkbox"
                          role="checkbox"
                          tabIndex={-1}
                          key={`${row?.project_name}-${row.language_code}-${row.project_name}`}
                        >
                          {columns.map((column, index) => {
                            const value = row[column.id];
                            return (
                              <React.Fragment
                                key={`table-row-cell-${row?.project_name}-${
                                  index + 4
                                }-${column.label}-${row.language_code}`}
                              >
                                {column.id === "projects" ? (
                                  <TableCell
                                    key={`${column.align}-${column.id}-${row.language_code}`}
                                    align="center"
                                    style={{
                                      color: "#191B23",
                                      padding: "5px 8px",
                                    }}
                                    className="d-flex align-items-center table-fixed-tb"
                                  >
                                    <div>
                                      <Checkbox
                                        sx={{
                                          color: "#C4C7CF",
                                          "&.Mui-checked": {
                                            color: "#365486",
                                          },
                                        }}
                                        className="p-1 me-2"
                                        checked={isKeywordSelected(
                                          row?.project_id
                                        )}
                                        onChange={(e) =>
                                          handleSelectProjectFromTable(row)
                                        }
                                      />
                                    </div>
                                    <div className="d-flex flex-column align-items-start">
                                      <div className="d-flex align-items-center">
                                        <Ellipsis trim="end" wMax={200}>
                                          <span style={{ color: "#365486" }}>
                                            {row?.project_name}
                                          </span>
                                        </Ellipsis>
                                        <img
                                          src={getCountryFlag(
                                            row?.project_location
                                          )}
                                          className="project_flag mx-1"
                                          alt={row?.project_location}
                                        />
                                        <OpenInNewIcon
                                          className="point project_redirect_icon"
                                          onClick={() =>
                                            handleProjectChange(row)
                                          }
                                        />
                                        {row?.sub_project?.length > 0 && (
                                          <IconButton
                                            aria-label="expand row"
                                            className="p-0"
                                            size="small"
                                            onClick={() =>
                                              handleCollapse(row.project_name)
                                            }
                                          >
                                            {open === row?.project_name ? (
                                              <KeyboardArrowUpIcon />
                                            ) : (
                                              <KeyboardArrowDownIcon />
                                            )}
                                          </IconButton>
                                        )}
                                      </div>
                                      <span
                                        className="font-medium heading-small project-lang"
                                        style={{ color: "#6C6E79" }}
                                      >
                                        ({getLanguageName(row.language_code)})
                                      </span>
                                    </div>
                                  </TableCell>
                                ) : (
                                  <TableCell
                                    key={column.id}
                                    align="center"
                                    style={{
                                      color: "#191B23",
                                      padding: "5px 8px",
                                    }}
                                  >
                                    {column.format && typeof value === "number"
                                      ? column.format(value)
                                      : value}
                                  </TableCell>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </TableRow>

                        {open === row?.project_name
                          ? row?.sub_project?.map((item, index) => {
                              return (
                                <TableRow
                                  key={`collapse-row-${row?.project_name}-${
                                    index + 3
                                  }-${row.language_code}`}
                                >
                                  {columns.map((column, index) => {
                                    const value = item[column.id];
                                    return (
                                      <React.Fragment
                                        key={`collapse-table-cell-${
                                          item?.project_location
                                        }-${index + 4}-${row.language_code}`}
                                      >
                                        {column.id === "projects" ? (
                                          <TableCell
                                            key={`${column.id}-${
                                              column.align
                                            }-${column.minWidth || index}`}
                                            align={column.align}
                                            style={{
                                              minWidth: column.minWidth,
                                              color: "#191B23",
                                            }}
                                            className="d-flex align-items-center table-fixed-tb"
                                          >
                                            <div
                                              style={{ width: "42px" }}
                                            ></div>

                                            {item?.project_location}
                                            <img
                                              src={getCountryFlag(
                                                item?.project_location
                                              )}
                                              alt={item?.project_location}
                                              className="project_flag mx-1"
                                            />
                                          </TableCell>
                                        ) : (
                                          <TableCell
                                            key={column.id}
                                            align={column.align}
                                            style={{
                                              minWidth: column.minWidth,
                                              color: "#191B23",
                                            }}
                                          >
                                            {column.format &&
                                            typeof value === "number"
                                              ? column.format(value)
                                              : value}
                                          </TableCell>
                                        )}
                                      </React.Fragment>
                                    );
                                  })}
                                </TableRow>
                              );
                            })
                          : null}
                      </React.Fragment>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </section>
    </>
  );
};

export default React.memo(ProjectTable);
